import { ArrowLeft, Play, Save } from 'lucide-react'

import { ActionFieldset, Button, Disclosure, SelectField, StatusMessage, TextField, formatAmount, formatKopecks } from '@krp/ui-kit'

import { APPLY_ACTION_LABELS, PRICE_TRANSFER_LABELS, detailOf, subjectOf } from '../model/labels'
import { priceOutcome } from '../model/price'
import { PRICE_TRANSFERS, type ApplyAction, type Divergence, type PriceTransfer, type ShownError, type TemplateField } from '../model/types'
import { ChangeBadge, ErrorBlock } from './parts'
import './sync.css'

/** Шаблон создания одного целевого источника: поля, которые он требует, и их значения по умолчанию из его настроек. */
export interface TargetTemplateVm {
  resourceId: string
  name: string
  status: 'loading' | 'failed' | 'ready'
  error: ShownError | null
  fields: TemplateField[]
}

export interface ApplyVm {
  sourceName: string
  rows: Divergence[]
  /** Куда уйдёт: имена источников-целей (у «вернуть» — сам источник окна). */
  targets: { resourceId: string; name: string }[]
  /** Действия, которые подходят отмеченным строкам. */
  actions: ApplyAction[]
  action: ApplyAction
  /** Чьё значение передаётся — выбор из источников, где оно есть. */
  fromChoices: { resourceId: string; name: string }[]
  from: string | null
  nameOf: (resourceId: string) => string
  priceTransfer: PriceTransfer
  /** Строки создают записи: окно показывает поля создания целей. */
  creating: boolean
  templates: TargetTemplateVm[]
  /** Значение поля создания «для всех» (уже с умолчанием источника). */
  commonValue: (targetId: string, key: string) => string
  /** Значение поля для одной строки: своё или общее. */
  rowValue: (rowId: number, targetId: string, key: string) => string
  /** Почему применить сейчас нельзя — по пунктам; пусто — можно. */
  blockers: string[]
  busy: boolean
  error: ShownError | null
  ruleBusy: boolean
  /** Почему правило цены сохранить нельзя; `null` — можно. */
  ruleBlocker: string | null
  ruleError: ShownError | null
  ruleSaved: boolean
}

export interface ApplyActions {
  back: () => void
  actionChanged: (action: ApplyAction) => void
  fromChanged: (resourceId: string) => void
  priceTransferChanged: (transfer: PriceTransfer) => void
  commonChanged: (targetId: string, key: string, value: string) => void
  rowChanged: (rowId: number, targetId: string, key: string, value: string) => void
  confirm: () => void
  saveRule: () => void
}

type FieldScope = 'all' | 'row'

function FieldInput({ field, value, onChange, scope }: { field: TemplateField; value: string; onChange: (value: string) => void; scope: FieldScope }) {
  const label = field.required ? `${field.label} (обязательно)` : field.label
  const options =
    field.type === 'choice'
      ? [{ value: '', label: 'Не выбрано' }, ...field.options]
      : field.type === 'boolean'
        ? [
            { value: '', label: 'Не выбрано' },
            { value: 'true', label: 'Да' },
            { value: 'false', label: 'Нет' },
          ]
        : null
  if (options !== null) {
    return scope === 'all' ? (
      <SelectField label={label} value={value} options={options} onChange={onChange} actionId="sync.divergences.create-field" />
    ) : (
      <SelectField label={label} value={value} options={options} onChange={onChange} actionId="sync.divergences.create-field-row" />
    )
  }
  return scope === 'all' ? (
    <TextField label={label} value={value} onChange={onChange} actionId="sync.divergences.create-field" />
  ) : (
    <TextField label={label} value={value} onChange={onChange} actionId="sync.divergences.create-field-row" />
  )
}

function Templates({ vm, actions }: { vm: ApplyVm; actions: ApplyActions }) {
  const ready = vm.templates.filter((template) => template.status === 'ready' && template.fields.length > 0)
  return (
    <section className="sync__panel" aria-label="Поля создания">
      <h3>Поля, которые нужны источнику при создании</h3>
      <p className="sync__note">
        Значения взяты из настроек «создавать по умолчанию» каждого источника. Поменяйте их сразу для всех строк или откройте строку ниже
        и поменяйте только для неё.
      </p>
      {vm.templates.map((template) => (
        <div key={template.resourceId} className="sync__panel">
          <p>
            <strong>{template.name}</strong>
          </p>
          {template.status === 'loading' && <StatusMessage kind="loading" title="Спрашиваем у источника, какие поля нужны…" />}
          {template.status === 'failed' && template.error !== null && <ErrorBlock error={template.error} />}
          {template.status === 'ready' &&
            (template.fields.length === 0 ? (
              <p className="sync__note">Источник берёт всё нужное из самой позиции.</p>
            ) : (
              <div className="sync-apply__fields">
                {template.fields.map((field) => (
                  <FieldInput key={field.key} field={field} value={vm.commonValue(template.resourceId, field.key)} onChange={(value) => actions.commonChanged(template.resourceId, field.key, value)} scope="all" />
                ))}
              </div>
            ))}
        </div>
      ))}
      {ready.length > 0 && (
        <ul className="sync-list" aria-label="Поля создания по строкам">
          {vm.rows.map((row) => (
            <li key={row.id}>
              <Disclosure summary={`Своё для строки: ${row.title}`} actionId="sync.divergences.create-row">
                {ready.map((template) => (
                  <div key={template.resourceId} className="sync__panel">
                    <p>
                      <strong>{template.name}</strong>
                    </p>
                    <div className="sync-apply__fields">
                      {template.fields.map((field) => (
                        <FieldInput
                          key={field.key}
                          field={field}
                          value={vm.rowValue(row.id, template.resourceId, field.key)}
                          onChange={(value) => actions.rowChanged(row.id, template.resourceId, field.key, value)}
                          scope="row"
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </Disclosure>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function Prices({ vm, actions }: { vm: ApplyVm; actions: ApplyActions }) {
  const prices = vm.rows.filter((row) => row.dataKind === 'price')
  return (
    <section className="sync__panel" aria-label="Цены">
      <h3>Цена и НДС</h3>
      <ActionFieldset actionId="sync.divergences.price-rule" legend="Как переносить цену между источником с НДС и без">
        <div className="sync__row" role="radiogroup" aria-label="Правило цены">
          {PRICE_TRANSFERS.map((transfer) => (
            <label key={transfer} className="sync__row">
              <input type="radio" name="sync-price-transfer" checked={vm.priceTransfer === transfer} onChange={() => actions.priceTransferChanged(transfer)} />
              {PRICE_TRANSFER_LABELS[transfer]}
            </label>
          ))}
        </div>
      </ActionFieldset>
      <div className="sync-table__scroll">
        <table className="sync-table">
          <caption className="sync__note">Было → станет по каждой цели</caption>
          <thead>
            <tr>
              <th scope="col">Товар</th>
              <th scope="col">Вид цены</th>
              <th scope="col">Источник</th>
              <th scope="col">Было</th>
              <th scope="col">Станет</th>
              <th scope="col">Как считается</th>
            </tr>
          </thead>
          <tbody>
            {prices.flatMap((row) => {
              const from = row.values.find((value) => value.resourceId === vm.from)
              return vm.targets.map((target) => {
                const there = row.values.find((value) => value.resourceId === target.resourceId)
                const outcome = priceOutcome(from, there, vm.priceTransfer)
                return (
                  <tr key={`${row.id}:${target.resourceId}`}>
                    <td>{row.title}</td>
                    <td>{there?.priceTypeName ?? there?.priceTypeKey ?? '—'}</td>
                    <td>{target.name}</td>
                    <td className="sync-apply__price">{outcome.before === null ? 'нет цены' : formatKopecks(outcome.before)}</td>
                    <td className="sync-apply__price">{outcome.after === null ? 'не перенесётся' : formatKopecks(outcome.after)}</td>
                    <td>{outcome.reason ?? ''}</td>
                  </tr>
                )
              })
            })}
          </tbody>
        </table>
      </div>
      <div className="sync__row">
        <Button
          variant="secondary"
          icon={<Save size={16} aria-hidden="true" />}
          busy={vm.ruleBusy}
          disabled={vm.ruleBlocker !== null}
          disabledReason={vm.ruleBlocker}
          onClick={actions.saveRule}
          actionId="sync.divergences.price-rule-save"
        >
          Сохранить правило без применения
        </Button>
        {vm.ruleSaved && (
          <p className="sync__note" role="status">
            Правило сохранено: расхождения цен пересчитаются по нему.
          </p>
        )}
      </div>
      {vm.ruleError !== null && <ErrorBlock error={vm.ruleError} />}
    </section>
  )
}

/**
 * Окно применения (RP-SYN8, план 3.4, 3.7): что и куда уйдёт, поля создания каждого целевого источника, массово заполненные его
 * умолчаниями, и правило цены с «было → станет». Ничего не уходит, пока человек не нажал «Применить».
 */
export function ApplyPanel({ vm, actions }: { vm: ApplyVm; actions: ApplyActions }) {
  const hasPrices = vm.action === 'copy' && vm.rows.some((row) => row.dataKind === 'price')
  return (
    <section aria-labelledby="sync-apply-title" className="sync">
      <header>
        <Button variant="ghost" icon={<ArrowLeft size={16} aria-hidden="true" />} onClick={actions.back} actionId="sync.divergences.apply-back">
          К списку
        </Button>
        <h2 id="sync-apply-title">Применить отмеченное: {vm.rows.length}</h2>
        <p className="sync__lead">
          Источник: {vm.sourceName}. Куда: {vm.targets.length === 0 ? 'не выбрано' : vm.targets.map((target) => target.name).join(', ')}.
        </p>
      </header>

      <section className="sync__panel" aria-label="Что сделать">
        <div className="sync__row">
          <SelectField
            label="Что сделать"
            value={vm.action}
            options={vm.actions.map((action) => ({ value: action, label: APPLY_ACTION_LABELS[action] }))}
            onChange={(value) => actions.actionChanged(vm.actions.find((action) => action === value) ?? 'copy')}
            disabled={vm.actions.length <= 1}
            disabledReason={vm.actions.length <= 1 ? 'Отмеченным строкам подходит одно действие.' : null}
            actionId="sync.divergences.apply-action"
          />
          {vm.action === 'copy' && vm.fromChoices.length === 0 && <p className="sync__note">Значение берётся из общего каталога.</p>}
          {vm.action === 'copy' && vm.fromChoices.length > 0 && (
            <SelectField
              label="Значение брать из"
              value={vm.from ?? ''}
              options={vm.fromChoices.map((choice) => ({ value: choice.resourceId, label: choice.name }))}
              onChange={actions.fromChanged}
              disabled={vm.fromChoices.length <= 1}
              disabledReason={vm.fromChoices.length <= 1 ? 'Значение есть только в одном источнике.' : null}
              actionId="sync.divergences.apply-from"
            />
          )}
        </div>
        {vm.action === 'delete' && (
          <p className="sync__note">
            Удаление — штатное для каждого источника: в 1С ставится пометка удаления, из кабинета AQSI товар удаляется, в своей базе уходит в
            архив. У штрихкодов и фото удаляется только это значение; цена не удаляется — перенесите верную.
          </p>
        )}
        <ul className="sync-list" aria-label="Строки к применению">
          {vm.rows.map((row) => (
            <li key={row.id} className="sync__row">
              <ChangeBadge change={row.type} subject={subjectOf(row)} />
              <span>{row.title}</span>
              {detailOf(row) !== null && <span className="sync__note">{detailOf(row)}</span>}
              {row.dataKind === 'price' && (
                <span className="sync__note">
                  {row.values
                    .filter((value) => value.present)
                    .map((value) => `${vm.nameOf(value.resourceId)}: ${formatAmount({ decimal: value.value })}`)
                    .join(' · ')}
                </span>
              )}
            </li>
          ))}
        </ul>
      </section>

      {vm.creating && <Templates vm={vm} actions={actions} />}
      {hasPrices && <Prices vm={vm} actions={actions} />}

      <section className="sync__panel sync-actions" aria-label="Применить">
        {vm.blockers.length > 0 && (
          <ul className="sync__note" aria-label="Что мешает применить">
            {vm.blockers.map((blocker) => (
              <li key={blocker}>{blocker}</li>
            ))}
          </ul>
        )}
        <Button
          variant="primary"
          icon={<Play size={16} aria-hidden="true" />}
          busy={vm.busy}
          disabled={vm.blockers.length > 0}
          disabledReason={vm.blockers[0] ?? null}
          onClick={actions.confirm}
          actionId="sync.divergences.apply-confirm"
        >
          Применить
        </Button>
        {vm.error !== null && <ErrorBlock error={vm.error} />}
      </section>
    </section>
  )
}
